import { CurrentQuoteState, LineItem, PackageBomProductDetails } from 'types';
import { CheckoutSavedData } from 'utils/helpers/checkout';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { CSAP_ELIGIBILITY_TIME_IN_DAYS } from 'utils/constants';
dayjs.extend(isSameOrBefore);

type CsapPartDetails = {
  [key: string]: {
    partName: string;
    partQuantity: number;
  };
};

export type CsapGroupRecord = Record<
  string,
  {
    pricePerUnit: number;
    totalQuantity: number;
    totalPrice: number;
    parts: CsapPartDetails;
  }
>;

export const createCsapGroup = (item: LineItem | PackageBomProductDetails): CsapGroupRecord => {
  return {
    [item.csapGroupDescription!]: {
      pricePerUnit: item.csapPricePerUnit!,
      totalQuantity: 0,
      totalPrice: 0,
      parts: {} as CsapPartDetails,
    },
  };
};

export const isDateEligibleForCsap = (shipDate: string) => {
  const configuredShipDate = dayjs(shipDate);
  const eligibilityTimeLimit = dayjs().add(CSAP_ELIGIBILITY_TIME_IN_DAYS, 'day');

  return configuredShipDate.isSameOrBefore(eligibilityTimeLimit, 'day');
};

export const getCsapEligibleParts = ({
  currentQuoteData,
  checkoutData,
}: {
  currentQuoteData: CurrentQuoteState;
  checkoutData: CheckoutSavedData;
}) => {
  const csapPricePerGroup = currentQuoteData?.items.reduce(
    (csapPricePerGroup, item) => {
      const lineEligibleForCsap = item.isEligibleForCSAP;
      const bomItemsEligibleForCsap =
        item.packageBom?.filter((bomItem) => bomItem.productDetails.isEligibleForCSAP) || [];

      if (!lineEligibleForCsap && !bomItemsEligibleForCsap.length) {
        return csapPricePerGroup;
      }

      if (lineEligibleForCsap) {
        csapPricePerGroup.eligibleParts.add(item.partNumber);

        const groupDescription = item.csapGroupDescription!;
        let groupDetails = csapPricePerGroup.csapRecords[groupDescription];

        if (!groupDetails) {
          groupDetails = createCsapGroup(item)[groupDescription];
        }

        if (!groupDetails.parts[item.partNumber]) {
          groupDetails.parts[item.partNumber] = { partName: item.partName, partQuantity: 0 };
        }

        groupDetails.parts[item.partNumber].partQuantity += item.quantity;
        groupDetails.totalQuantity += item.quantity;

        csapPricePerGroup.csapRecords[groupDescription] = groupDetails;
      }

      bomItemsEligibleForCsap.forEach((bomItem) => {
        csapPricePerGroup.eligibleParts.add(bomItem.partNumber);

        const groupDescription = bomItem.productDetails.csapGroupDescription!;
        let groupDetails = csapPricePerGroup.csapRecords[groupDescription];

        if (!groupDetails) {
          groupDetails = createCsapGroup(bomItem.productDetails)[groupDescription];
        }

        if (!groupDetails.parts[bomItem.partNumber]) {
          groupDetails.parts[bomItem.partNumber] = {
            partName: bomItem.productDetails.partName,
            partQuantity: 0,
          };
        }

        groupDetails.parts[bomItem.partNumber].partQuantity += item.quantity * bomItem.quantity;
        groupDetails.totalQuantity += item.quantity * bomItem.quantity;

        csapPricePerGroup.csapRecords[groupDescription] = groupDetails;
      });

      return csapPricePerGroup;
    },
    { csapRecords: {}, eligibleParts: new Set() } as {
      csapRecords: CsapGroupRecord;
      eligibleParts: Set<string>;
    }
  );

  const hasScheduledItemsAfterCsapEligibilityPeriod =
    checkoutData.shippingInfo?.shipmentSchedule?.some((schedule) => {
      // If date is in eligibility period, we're good
      if (isDateEligibleForCsap(schedule.shipDate)) {
        return false;
      }

      // If date is not eligible and the schedule has eligible parts, we need to disable CSAP enrollment
      const hasCsapEligibleParts = schedule.lines.some(
        (item) => item.quantity > 0 && csapPricePerGroup.eligibleParts.has(item.partNumber)
      );

      return hasCsapEligibleParts;
    });

  if (hasScheduledItemsAfterCsapEligibilityPeriod) {
    return { csapRecords: {}, eligibleParts: new Set() };
  }

  return csapPricePerGroup;
};
